/**
 * Task 2E — PlotexImpostorMaterial
 *
 * View-dependent billboard shader for Plotex impostors.
 *
 * Samples an 8-view atlas based on the camera's azimuth around the
 * instance and blends the two nearest views with a smoothstep factor,
 * producing a convincing "turning" effect as the camera rotates around
 * the impostor without the visible popping that a nearest-neighbor
 * lookup would cause every 45 degrees.
 *
 * Atlas layout (assumed): 8 views laid out in a horizontal strip, so
 * view N occupies `u in [N/8, (N+1)/8]` and `v in [0, 1]`. The
 * Blender-side bake op produces this layout — we match it exactly.
 * If a future bake uses a different count, pass it via the
 * `uViewCount` uniform (see the `setViewCount` helper below).
 *
 * Per-frame GPU work: one cheap atan in the vertex shader, two
 * texture fetches + a mix in the fragment shader. Cheaper than
 * rendering the original geometry for anything beyond ~50m, which
 * is where the LOD manager hands meshes off to impostors.
 *
 * Scope note: this file is the material class only. Wiring it up to
 * detect `is_impostor_source` meshes, building InstancedMeshes of
 * camera-facing quads, and swapping between real geometry and
 * impostor based on distance — all that belongs to a later wave
 * (Task 3C examples / dedicated follow-up). See the
 * `[plotex-hook:impostor]` anchor in `main.ts`.
 */

import {
  ShaderMaterial,
  type Texture,
  Vector3,
} from 'three';

/**
 * Custom `ShaderMaterial` that renders a camera-facing quad as one
 * instance of a baked 8-view impostor atlas. Picks the two nearest
 * views each frame and blends them with smoothstep, so distant
 * vegetation "turns" smoothly as the camera orbits.
 */
export class PlotexImpostorMaterial extends ShaderMaterial {
  constructor(atlas: Texture) {
    super({
      uniforms: {
        // The baked 8-view atlas. One column of the atlas per view.
        uAtlas: { value: atlas },
        // Camera world position. The driver (see `updateCameraPos`)
        // must refresh this every frame — the shader uses it to
        // compute the per-instance azimuth to the camera. If you
        // forget to update it, every impostor freezes on whatever
        // view was picked during the last call and the "turning"
        // illusion goes away.
        uCameraPos: { value: new Vector3() },
        // Atlas slice count. Defaults to 8 to match the Blender
        // plugin's `_bake_impostor_atlas`. Exposed as a uniform so
        // we don't have to recompile the shader for a 16-view bake.
        uViewCount: { value: 8 },
      },
      vertexShader: /* glsl */ `
        // The per-instance world placement arrives via the
        // InstancedMesh built-in \`instanceMatrix\`. We bake the quad
        // at origin locally, then the instance matrix translates it
        // to the actual tree / shrub location.
        uniform vec3 uCameraPos;
        uniform float uViewCount;

        // Interpolated atlas UVs for the two nearest views, plus the
        // smoothstep blend factor that controls how much of view 1
        // bleeds into view 0. All three are computed per-vertex but
        // read per-fragment — the values are constant across the
        // quad (they depend only on the instance, not the vertex),
        // so hardware interpolation here is a no-op in practice.
        varying vec2 vUv0;
        varying vec2 vUv1;
        varying float vBlend;

        void main() {
          // The instance's WORLD position — i.e., where this tree
          // actually stands in the scene. Extracted by pushing the
          // origin through modelMatrix * instanceMatrix (this folds
          // out to the translation column at compile time).
          vec4 worldPos = modelMatrix * instanceMatrix * vec4(0.0, 0.0, 0.0, 1.0);

          // Direction from the instance to the camera, projected to
          // the XZ plane. Three.js is Y-up with the ground on XZ, so
          // "which way is the camera standing" only depends on those
          // two axes — the height difference would tilt the quad if
          // we cared, but impostors cheat and always face horizontally.
          // NOTE: \`dir.y\` below is the swizzled vec2's second
          // component — it holds the world-space Z delta, NOT world Y.
          vec2 dir = uCameraPos.xz - worldPos.xz;

          // Azimuth angle. \`atan(y, x)\` (two-argument form) returns
          // a signed angle in (-pi, pi]; shift negative values up by
          // 2pi so we get a clean [0, 2pi) wraparound. This is the
          // angle from the instance to the camera measured around the
          // +X axis, counter-clockwise when viewed from above.
          float az = atan(dir.y, dir.x);
          if (az < 0.0) az += 6.283185307;

          // BILLBOARD THE QUAD around world-Y so it always faces the
          // camera horizontally. PlaneGeometry's verts live in the XY
          // plane with +Y as "up"; we want the quad's +X to run along
          // the camera's horizontal right-direction, and its +Y to
          // stay aligned with world up. Without this rotation the
          // quad keeps its native +Z normal and ends up lying flat on
          // the ground (author-facing original bug).
          //
          //   right = perpendicular to camera direction in XZ plane
          //         = (-sin(az), 0, cos(az))  when az runs around +X→+Z
          //   up    = world up = (0, 1, 0)
          //
          // Local position.x scales along right, position.y scales
          // along up, position.z is ignored (PlaneGeometry has z=0).
          // The billboard offset is added to the instance world
          // position; the view matrix handles the rest of the
          // camera transform below.
          vec3 right = vec3(-sin(az), 0.0, cos(az));
          vec3 up = vec3(0.0, 1.0, 0.0);
          vec3 billboardWorld = worldPos.xyz
            + position.x * right
            + position.y * up;

          vec4 mvPosition = viewMatrix * vec4(billboardWorld, 1.0);
          gl_Position = projectionMatrix * mvPosition;

          // Normalize the azimuth into "view slots": the full circle
          // [0, 2pi) becomes [0, uViewCount). Each integer is a baked
          // view; fractional part is how far past that view the
          // camera has rotated. With uViewCount = 8 that gives 45deg
          // per slot, matching the Blender bake.
          float slot = az / 6.283185307 * uViewCount;

          // Two nearest views: \`i0\` just before us, \`i1\` just after.
          // The modulo wraps view 7 back to view 0 so the seam between
          // the last and first bake is seamless.
          float i0 = floor(slot);
          float i1 = mod(i0 + 1.0, uViewCount);
          float t = slot - i0;

          // Smoothstep softens the linear crossfade so we don't see
          // a hard ramp exactly at the view boundary — the human eye
          // picks up linear transitions as "popping" even when the
          // images are very close, and smoothstep's ease-in/ease-out
          // hides the cut.
          vBlend = smoothstep(0.0, 1.0, t);

          // Atlas UV math. The quad's source \`uv\` attribute is the
          // standard [0,1]^2. We compress the u axis into one slice's
          // worth of the atlas (sliceWidth = 1 / uViewCount) and then
          // offset by the slice index: slice N lives in
          // [N * sliceWidth, (N+1) * sliceWidth]. The v axis passes
          // through unchanged because all views share the full height.
          float sliceWidth = 1.0 / uViewCount;
          vUv0 = vec2(uv.x * sliceWidth + i0 * sliceWidth, uv.y);
          vUv1 = vec2(uv.x * sliceWidth + i1 * sliceWidth, uv.y);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform sampler2D uAtlas;

        varying vec2 vUv0;
        varying vec2 vUv1;
        varying float vBlend;

        void main() {
          // Two cheap fetches into the same texture — hardware
          // cache-friendly because the two UVs are at most one
          // slice apart, so both samples land in nearby texels.
          vec4 tex0 = texture2D(uAtlas, vUv0);
          vec4 tex1 = texture2D(uAtlas, vUv1);

          // Straight linear mix with the smoothstep-softened factor.
          // If uViewCount grows, only the ratio between adjacent
          // samples changes — this line is forever.
          vec4 blended = mix(tex0, tex1, vBlend);

          // Alpha-test cutout. The atlas background is transparent
          // around the silhouette of the baked mesh. We discard
          // below 0.5 so we get a clean hard edge (no sorting
          // required) — cheaper than real transparency and doesn't
          // interact with the depth buffer in surprising ways.
          if (blended.a < 0.5) discard;
          gl_FragColor = blended;
        }
      `,
      // We use alpha-test (\`discard\` in the fragment shader), not
      // alpha blending. Setting transparent=false keeps the impostor
      // quads participating in the opaque pass, which means they
      // write depth and sort correctly with the rest of the scene
      // — critical when a real palm tree is standing next to a
      // distant impostor palm tree and we want neither to bleed
      // through the other.
      transparent: false,
    });
  }

  /**
   * Update the camera position uniform. Call this per frame from
   * whatever driver wires up the impostor InstancedMesh. Without
   * this call, the impostor won't "turn" with the camera — the
   * shader uses `uCameraPos` to compute each instance's azimuth,
   * so if the uniform is stale the impostors freeze on an old view.
   */
  updateCameraPos(worldPos: Vector3): void {
    (this.uniforms.uCameraPos.value as Vector3).copy(worldPos);
  }

  /**
   * Override the atlas slice count. Defaults to 8 to match the
   * Blender bake operator. Future bakes that use a different
   * number of views (e.g. 16 for higher quality) can call this
   * instead of writing a new material class.
   */
  setViewCount(count: number): void {
    this.uniforms.uViewCount.value = count;
  }

  /**
   * Swap in a new atlas texture without recreating the material.
   * Useful if the same InstancedMesh is reused across lifecycle
   * events (e.g. the source asset is replaced on a live edit).
   */
  setAtlas(atlas: Texture): void {
    this.uniforms.uAtlas.value = atlas;
  }

  /**
   * Static factory — convenience for the common case where you
   * already have a Texture in hand and want a ready-to-use
   * material with no other configuration.
   */
  static createFor(atlas: Texture): PlotexImpostorMaterial {
    return new PlotexImpostorMaterial(atlas);
  }
}
